import type { ColumnDef, RowData } from '@tanstack/react-table'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useEffect, useRef } from 'react'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../_components/ui/table'
import { cn } from '../../_lib/utils'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    width?: string
  }
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  className?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
}: DataTableProps<TData, TValue>) {
  const bodyScrollRef = useRef<HTMLDivElement>(null)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  useEffect(() => {
    bodyScrollRef.current?.scrollTo({ top: 0 })
  }, [data])

  const colGroup = (
    <colgroup>
      {table.getAllColumns().map((col) => (
        <col
          key={col.id}
          style={col.columnDef.meta?.width ? { width: col.columnDef.meta.width } : undefined}
        />
      ))}
    </colgroup>
  )

  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-hidden rounded-[8px] border border-brand-grayscale-4',
        className,
      )}
    >
      <div ref={bodyScrollRef} className="h-full overflow-auto">
        <table className="min-w-[768px] w-full table-fixed border-separate border-spacing-0 text-sm">
          {colGroup}
          <TableHeader className="sticky top-0 z-10 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)] [&_tr]:border-0">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-0 bg-brand-grayscale-2">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="h-16 whitespace-nowrap bg-brand-grayscale-2 px-8"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="[&_tr:first-child_td]:pt-1">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  className="border-0 bg-brand-grayscale-0 hover:bg-brand-grayscale-2"
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="border-b border-brand-grayscale-4 p-0">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Nenhum resultado encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>
    </div>
  )
}
